import { ObjectType } from "@/components/atoms/Drawable/ObjectTypeIcon/ObjectTypeIcon";
import { replaceUrlParams } from "@/core/string";
import { Permissions } from "@/shared/domain";
import { Identity } from "@/shared/domain/identity";
import { AppRoute, DetailPageState, InsuredGoodDetailPageState } from "@/shared/domain/navigation";

import { getSpApi, requestConfig } from "./apiRoot";
import { UrlManager } from "./UrlManager";

export interface SearchResult {
    type: ObjectType;
    id: number;
    name: string;
    description?: string | null;
    url: string;
    state: any;
    isAccessible: boolean;
}

export enum SearchContext {
    All = "0",
    Party = "2",
    Contract = "3",
    Claim = "4",
    RiskObject = "5",
}

const searchContextToObjectType = (context: string): ObjectType => {
    switch (context) {
        case SearchContext.Party:
            return "policyholder";
        case SearchContext.Contract:
            return "contract";
        case SearchContext.Claim:
            return "claim";
        case SearchContext.RiskObject:
            return "insuredgood";
    }
    throw new Error(`Unknown context : ${context}`);
};

const searchContextToPermission = (context: string): Permissions => {
    switch (context) {
        case SearchContext.Party:
            return Permissions.ViewInformationDetail;
        case SearchContext.Contract:
            return Permissions.ViewContractsDetails;
        case SearchContext.Claim:
            return Permissions.ViewClaimsDetails;
        case SearchContext.RiskObject:
            return Permissions.ViewRisksDetails;
    }
    throw new Error(`Unknown context : ${context}`);
};

const objectTypeToRouteSegment = (objectType: SearchContext): string => {
    switch (objectType) {
        case SearchContext.Party:
            return AppRoute.PolicyholderDetail;
        case SearchContext.Contract:
            return AppRoute.ContractDetails;
        case SearchContext.Claim:
            return AppRoute.ClaimDetails;
        case SearchContext.RiskObject:
            return AppRoute.InsuredGoodDetails;
    }
};

const buildUrl = (objectType: SearchContext, id: number): string => {
    return replaceUrlParams(objectTypeToRouteSegment(objectType), { id });
};

const buildState = (objectType: SearchContext, policyholderId: number, subContextType: string): any => {
    switch (objectType) {
        case SearchContext.RiskObject:
            return {
                insuredGoodType: subContextType,
                policyholderId: policyholderId,
            } as InsuredGoodDetailPageState;
        case SearchContext.Contract:
        case SearchContext.Claim:
        case SearchContext.Party:
            return {
                policyholderId,
            } as DetailPageState;
    }
};

const searchResultsMapper = (source: MyBrokerApi.SearchResult[], user: Identity): SearchResult[] => {
    return source.map((res) => {
        return {
            type: searchContextToObjectType(res.contextType),
            id: res.objectId,
            name: `${res.type} - ${res.reference}`,
            description: res.info,
            isAccessible: user.hasPermission(searchContextToPermission(res.contextType), res.policyholderId),
            url: buildUrl(res.contextType as SearchContext, res.objectId),
            state: buildState(res.contextType as SearchContext, res.policyholderId, res.subContextType),
        };
    });
};

export const getSearchResult = async (variables: {
    term: string;
    user: Identity;
    policyholders: number[];
    searchContext: SearchContext;
}): Promise<SearchResult[]> => {
    const axiosRequestConfig = requestConfig<Paths.ApiSearch.Get.QueryParameters>({
        params: {
            context: variables.searchContext,
            criteria: variables.term,
            policyholderIds: variables.policyholders,
        },
    });

    const { data } = await getSpApi().get<MyBrokerApi.SearchResult[]>(UrlManager.search(), axiosRequestConfig);
    return searchResultsMapper(data, variables.user);
};
